/* eslint-disable no-shadow */
import CustomerModel from '@/common/models/customer-model';
import ContactModel from '@/common/models/contact-model';
import PaymentInstrumentExpandedModel from '@/common/models/payment-instrument-expanded-model';
import RESOURCES from '@/common/auth/definitions/resources';

export const state = {
    customer: null,
    contacts: [],
    customFieldsSchema: [],
    customFields: null,
    lifetimeSummaryMetrics: [],
    paymentInstruments: [],
    paymentCardsCustomFields: null,
    isSegmentPanelOpen: false,
};

export const mutations = {
    setState(state, {property, payload}) {
        state[property] = payload;
    },
    updateLeadSource(state, leadSource) {
        state.customer.leadSource = leadSource;
    },
    addPaymentInstrument(state, instrument) {
        state.paymentInstruments.push(instrument);
    },
    removePaymentInstrument(state, instrument) {
        state.paymentInstruments = state.paymentInstruments
            .filter(item => item.id !== instrument.id);
    },
};

export const actions = ({api}) => ({
    set({commit}, payload) {
        commit('setState', {
            property: 'customer',
            payload: new CustomerModel(payload),
        });
    },
    async fetch({commit}, payload) {
        try {
            const {params} = payload;
            const {fields: customerFields} = await api(({customers}) => customers.get(params));
            const fetchedCustomer = new CustomerModel(customerFields);
            commit('setState', {
                property: 'customer',
                payload: fetchedCustomer,
            });
            return fetchedCustomer;
        } catch (error) {
            throw error;
        }
    },
    async updateCustomer({state, dispatch}, {data, params}) {
        return new Promise(async (resolve, reject) => {
            try {
                const {id} = state.customer;
                await api(({customers}) => customers.update({
                    id,
                    data,
                    params,
                }));
                const response = await dispatch('fetch', {
                    params: {
                        id,
                        expand: 'leadSource,defaultPaymentInstrument',
                    },
                });
                resolve(response);
            } catch (error) {
                reject(error);
            }
        });
    },
    setContacts({commit}, payload) {
        commit('setState', {
            property: 'contacts',
            payload: payload.map(contact => new ContactModel(contact.fields)),
        });
    },
    fetchCustomerContacts({state, dispatch}) {
        return new Promise(async (resolve, reject) => {
            try {
                const contactsParams = {
                    filter: `customerId:${state.customer.id}`,
                };
                const {
                    items: contactItems,
                } = await api(({contacts}) => contacts.getAll(contactsParams));
                dispatch('setContacts', contactItems);
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    },
    async addTag({state}, name) {
        const params = {
            id: state.customer.id,
            tag: name,
        };
        await api(({tags}) => tags.tagCustomer(params));
    },
    async removeTag({state}, name) {
        const params = {
            id: state.customer.id,
            tag: name,
        };
        await api(({tags}) => tags.untagCustomer(params));
    },
    async updateTags({dispatch}, {
        created, deleted, added,
    }) {
        if (created.length) {
            await Promise.all([
                ...created.map(name => api(({tags}) => tags.create({name}))),
            ]);
        }
        // modify customer tags list
        await Promise.all([
            ...deleted.map(name => dispatch('removeTag', name)),
            ...[...created, ...added].map(name => dispatch('addTag', name)),
        ]);
    },
    addPaymentInstrument({commit}, instrument) {
        commit('addPaymentInstrument', new PaymentInstrumentExpandedModel(instrument));
    },
    removePaymentInstrument({commit}, instrument) {
        commit('removePaymentInstrument', instrument);
    },
    updatePaymentInstrument({commit, state}, instrument) {
        const instrumentToUpdate = state.paymentInstruments
            .find(item => item.id === instrument.id);
        if (instrumentToUpdate) {
            commit('removePaymentInstrument', instrumentToUpdate);
            commit('addPaymentInstrument', new PaymentInstrumentExpandedModel({
                ...instrumentToUpdate,
                ...instrument,
            }));
        }
    },
    async fetchPaymentInstruments({state, commit}) {
        const processPaymentInstruments = ([bankAccounts, paymentCards, paypalAccounts]) => {
            const instrumentTypeTuple = [
                [bankAccounts],
                [paymentCards],
                [paypalAccounts],
            ];
            const reduceCallback = (allInstruments, [{items}]) => {
                const paymentInstruments = items
                    .map(({fields}) => new PaymentInstrumentExpandedModel(fields));
                return allInstruments.concat(paymentInstruments);
            };
            return instrumentTypeTuple.reduce(reduceCallback, []);
        };

        return new Promise(async (resolve, reject) => {
            try {
                const params = {
                    filter: `customerId:${state.customer.id};status:active,inactive,verification-needed,expired`,
                    sort: `-createdTime`,
                };
                const paymentCardsCustomFieldsParam = {
                    resource: RESOURCES.paymentCards,
                };
                const [
                    bankAccounts,
                    paymentCards,
                    paypalAccounts,
                    {items: customFieldsItems},
                ] = await Promise.all([
                    api(({bankAccounts}) => bankAccounts.getAll(params)),
                    api(({paymentCards}) => paymentCards.getAll(params)),
                    api(({paypalAccounts}) => paypalAccounts.getAll(params)),
                    api(({customFields}) => customFields.getAll(paymentCardsCustomFieldsParam)),
                ]);
                commit('setState', {
                    property: 'paymentInstruments',
                    payload: processPaymentInstruments([
                        bankAccounts,
                        paymentCards,
                        paypalAccounts,
                    ]),
                });
                commit('setState', {
                    property: 'paymentCardsCustomFields',
                    payload: customFieldsItems,
                });
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    },
    async updateLeadSources({state, commit}, leadSource) {
        const params = {
            id: state.customer.id,
            data: leadSource.getPayload(),
        };
        await api(({customers}) => customers.updateLeadSource(params));
        commit('updateLeadSource', leadSource);
    },
    async fetchLifetimeSummaryMetrics({state, commit}) {
        return new Promise(async (resolve, reject) => {
            try {
                const customer = await api(({customers}) => customers
                    .getCustomerLifetimeSummaryMetrics({customerId: state.customer.id}));
                commit('setState', {
                    property: 'lifetimeSummaryMetrics',
                    payload: customer.fields,
                });
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    },
    async fetchCustomFieldsSchema({commit}) {
        const params = {
            resource: RESOURCES.customers,
        };
        try {
            const {items} = await api(({customFields}) => customFields
                .allowedOnly.getAll(params));
            commit('setState', {
                property: 'customFieldsSchema',
                payload: items,
            });
        } catch (error) {
            throw error;
        }
    },
    setSegmentPanelOpen({commit}, isOpen) {
        commit('setState', {
            property: 'isSegmentPanelOpen',
            payload: isOpen,
        });
    },
});
